import { useHistory } from "react-router-dom";
import { Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { BiLogInCircle } from "react-icons/bi";
import { useEffect } from "react";

import { ESupportedGames, IClip, IClipNode, IReactable } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import { Button } from "MVVM/Components/Button";
import { URLS } from "MVVM/Utilities/Maps";
import { DynamicSignupOptions } from "MVVM/Views/Authentication/Components/DynamicSignupOptions";

interface LoginUpsellProps {
  clip?: IClip | IClipNode | (IClip & IReactable);
  game?: ESupportedGames;
  ctaText?: string;
  backgroundImageUrl?: string;
}

const getClipThumbnailUrl = (
  clip: IClip | IClipNode | (IClip & IReactable) | undefined,
): string | undefined => {
  if (!clip) return undefined;

  if ("thumbnailStandardUrl" in clip) {
    return clip.thumbnailStandardUrl;
  }

  if ("thumbnailUrl" in clip) {
    return clip.thumbnailUrl;
  }

  return undefined;
};

export const LoginUpsell = ({
  clip,
  game,
  ctaText,
  backgroundImageUrl,
}: LoginUpsellProps = {}) => {
  const history = useHistory();
  const bannerImageUrl =
    backgroundImageUrl ||
    getClipThumbnailUrl(clip) ||
    `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/profile/upsell-modal/banner.png`;
  const isClipContext = !!clip;

  useEffect(() => {
    analytics.track("Login Upsell", {
      action: "display",
      friendClip: false,
      context: isClipContext ? "clip" : "profile",
    });
  }, [isClipContext]);

  const handleLogin = () => {
    analytics.track("Login Upsell - Login Click", {
      context: isClipContext ? "clip_login_upsell" : "profile_login_upsell",
    });

    const currentPath = window.location.pathname + window.location.search;
    history.push(`${URLS.LOGIN}?redirect=${encodeURIComponent(currentPath)}`);
  };

  const handleSignup = () => {
    analytics.track("Login Upsell - Signup Click", {
      context: isClipContext ? "clip_login_upsell" : "profile_login_upsell",
    });

    const currentPath = window.location.pathname + window.location.search;
    history.push(`/register?redirect=${encodeURIComponent(currentPath)}`);
  };

  const renderButton = () => {
    if (game) return <DynamicSignupOptions game={game} />;

    return (
      <PrimaryButton size="small" onClick={handleLogin}>
        <Flex
          alignItems="center"
          justifyContent="center"
          style={{ gap: "8px" }}
        >
          <BiLogInCircle size={18} />
          Login
        </Flex>
      </PrimaryButton>
    );
  };

  const getTitleText = () => {
    if (ctaText) return `Login to ${ctaText} this Clip!`;

    if (isClipContext) return "Login for the best viewing experience!";

    return "Login for the Best Experience!";
  };

  return (
    <Container>
      <ImageBanner backgroundImageUrl={bannerImageUrl} isClip={isClipContext}>
        {isClipContext && clip && (
          <ClipInfoOverlay>
            <ClipTitle>{clip.title}</ClipTitle>
          </ClipInfoOverlay>
        )}
      </ImageBanner>
      <ContentSection>
        <Flex flexDirection="column" textAlign="center" style={{ gap: "16px" }}>
          <Title color="ice" variant="text.titleSmall">
            {getTitleText()}
          </Title>
          <Subtitle color="chalk" variant="text.paragraphMedium">
            {isClipContext ? (
              <>
                Log in to Allstar to{" "}
                <FeatureHighlight as="span">comment</FeatureHighlight>,{" "}
                <FeatureHighlight as="span">react</FeatureHighlight> and{" "}
                <FeatureHighlight as="span">
                  download this clip
                </FeatureHighlight>
                , or <FeatureHighlight as="span">Remix it</FeatureHighlight> to
                add your own filters, effects & music!
              </>
            ) : (
              <>
                Login to access all of the best features of Allstar profiles.{" "}
                <FeatureHighlight as="span">Add your socials</FeatureHighlight>,{" "}
                <FeatureHighlight as="span">
                  customize your look
                </FeatureHighlight>{" "}
                and{" "}
                <FeatureHighlight as="span">
                  pin your favorite clips
                </FeatureHighlight>
                .
              </>
            )}
          </Subtitle>
        </Flex>
        {renderButton()}
        <Flex style={{ gap: 5 }} justifyContent="center">
          <Text color="chalk" variant={"text.labelMedium"}>
            Don't have an account?
          </Text>
          <SecondaryText
            variant={"text.labelMedium"}
            color="envy"
            onClick={handleSignup}
          >
            Sign up
          </SecondaryText>
        </Flex>
      </ContentSection>
    </Container>
  );
};

const Container = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.ned};
  border-radius: 14px;
  border: ${({ theme }) => `1px solid ${theme.colors.envyAlpha20}`};
  box-shadow: ${({ theme }) => `0px 0px 16px 0px ${theme.colors.envyAlpha20}`};
  flex-direction: column;
  position: relative;
  text-align: center;
  width: 448px;

  @media (max-width: 500px) {
    margin: 20px;
    width: 390px;
  }

  @media (max-width: 425px) {
    width: calc(100vw - 40px);
  }
`;

const ImageBanner = styled(Flex)<{
  backgroundImageUrl: string;
  isClip?: boolean;
}>`
  align-items: center;
  background-image: ${({ isClip }) =>
      isClip
        ? `linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.4) 50%,
      var(--Gray-750, #1c2a34) 100%
    )`
        : `linear-gradient(
      to bottom,
      transparent,
      var(--Gray-750, #1c2a34)
    )`},
    url(${({ backgroundImageUrl }) => backgroundImageUrl});
  background-position: center;
  background-size: cover;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
  height: 192px;
  justify-content: center;
  padding-bottom: 16px;
  position: relative;
  width: 100%;
`;

const ClipInfoOverlay = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  z-index: 1;
`;

const ClipTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
  text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.6);
`;

const ContentSection = styled(Flex)`
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const Title = styled(Text)`
  @media (max-width: 425px) {
    font-size: 24px;
    line-height: 28px;
  }
`;

const Subtitle = styled(Text)`
  @media (max-width: 425px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${({ theme }) => `${theme.colors.envy}20`};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.envy};
  color: ${({ theme }) => theme.colors.envy};
  min-width: 160px;
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.envy};
    color: var(--Ice-100, var(--colors-ice-500100, #f7fcfd));
  }
`;

const SecondaryText = styled(Text)`
  cursor: pointer;
  margin-right: 15px;
  min-width: 55px;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 425px) {
    margin-right: 0px;
  }
`;

const FeatureHighlight = styled(Text)`
  color: ${({ theme }) => theme.colors.darkerEnvy};
  font-weight: 600;
`;
